import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Music, Settings, Volume2, VolumeX, X } from 'lucide-react';
import { useSound } from 'context/SoundProvider';

interface HeaderSettingsPopoverProps {
  className?: string;
}

export default function HeaderSettingsPopover({ className = '' }: HeaderSettingsPopoverProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; right: number } | null>(null);

  const { muted, musicEnabled, volume, toggleMute, toggleMusic, setVolume, unlock } = useSound();

  const close = useCallback(() => setOpen(false), []);

  const updatePanelPos = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setPanelPos({
      top: rect.bottom + 8,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setPanelPos(null);
      return;
    }
    updatePanelPos();
    window.addEventListener('resize', updatePanelPos);
    window.addEventListener('scroll', updatePanelPos, true);
    return () => {
      window.removeEventListener('resize', updatePanelPos);
      window.removeEventListener('scroll', updatePanelPos, true);
    };
  }, [open, updatePanelPos]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      close();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('touchstart', onPointerDown, { passive: true });
    }, 0);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  const popover =
    open && panelPos
      ? createPortal(
          <div
            ref={panelRef}
            className="tp-header-settings-popover"
            role="dialog"
            aria-label="Settings"
            style={{ top: panelPos.top, right: panelPos.right }}
          >
            <div className="tp-header-settings-popover-head">
              <span>Settings</span>
              <button
                type="button"
                className="tp-header-settings-popover-close"
                onClick={close}
                aria-label="Close settings"
              >
                <X size={16} />
              </button>
            </div>

            <div className="tp-header-settings-popover-body">
              <div className="tp-header-settings-row">
                <div className="tp-header-settings-row-label">
                  {muted ? (
                    <VolumeX size={16} className="text-white/50" />
                  ) : (
                    <Volume2 size={16} className="text-tp-gold" />
                  )}
                  <div>
                    <p className="tp-header-settings-row-title">Sound effects</p>
                    <p className="tp-header-settings-row-hint">Deal, chips, timer</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="tp-header-settings-toggle"
                  onClick={() => {
                    unlock();
                    toggleMute();
                  }}
                >
                  {muted ? 'Unmute' : 'Mute'}
                </button>
              </div>

              <div className="tp-header-settings-volume">
                <label className="tp-header-settings-volume-label">
                  <span>Volume</span>
                  <span>{Math.round(volume * 100)}%</span>
                </label>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={Math.round(volume * 100)}
                  onChange={(e) => {
                    unlock();
                    setVolume(Number(e.target.value) / 100);
                  }}
                  className="tp-volume-slider w-full"
                />
              </div>

              <div className="tp-header-settings-row tp-header-settings-row-border">
                <div className="tp-header-settings-row-label">
                  <Music size={16} className={musicEnabled ? 'text-tp-gold' : 'text-white/50'} />
                  <div>
                    <p className="tp-header-settings-row-title">Background music</p>
                    <p className="tp-header-settings-row-hint">Ambient table music</p>
                  </div>
                </div>
                <button
                  type="button"
                  className={`tp-header-settings-toggle ${musicEnabled ? 'tp-header-settings-toggle-on' : ''}`}
                  onClick={() => {
                    unlock();
                    toggleMusic();
                  }}
                >
                  {musicEnabled ? 'On' : 'Off'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          unlock();
          setOpen((prev) => !prev);
        }}
        className={[
          'tp-glass-btn tp-header-icon-btn tp-header-settings-btn flex items-center justify-center rounded-xl',
          open ? 'text-tp-gold ring-1 ring-tp-gold/40' : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label="Settings"
        aria-expanded={open}
      >
        <Settings size={17} />
      </button>
      {popover}
    </>
  );
}
